import React, { useEffect, useState } from "react";
import Alert from "react-bootstrap/Alert";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import { Link } from "react-router-dom";
import FlashAlert from "../../Components/FlashAlert/FlashAlert";
import { DeleteIcon, EditIcon, ViewIcon } from "../../Components/Icons/ActionIcons";
import apiService from "../../services/api.service";

const DashboardApplications = () => {
  const [applications, setApplications] = useState([]);
  const [gestori, setGestori] = useState([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(true);
  const [appToDelete, setAppToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [appsRes, gestoriRes] = await Promise.all([
        apiService.getApplications(),
        apiService.getGestori().catch(() => ({ data: [] })),
      ]);
      setApplications(appsRes.data || []);
      setGestori(gestoriRes.data || []);
    } catch {
      setError("Impossibile caricare le applicazioni.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const closeDeleteModal = () => {
    if (deleting) return;
    setAppToDelete(null);
    setDeleteError("");
  };

  const handleDelete = async () => {
    if (!appToDelete) return;
    setDeleting(true);
    setDeleteError("");
    try {
      await apiService.deleteApplication(appToDelete.id);
      setSuccess(`Applicazione "${appToDelete.name}" eliminata.`);
      setAppToDelete(null);
      await load();
    } catch (err) {
      setDeleteError(err.response?.data?.detail || "Eliminazione non riuscita.");
    } finally {
      setDeleting(false);
    }
  };

  const gestoriForApp = (appId) =>
    gestori.filter((g) => String(g.application_id) === String(appId));

  return (
    <div className="dashboard-card">
      <div style={{ display: "flex", justifyContent: "space-between", gap: 16, alignItems: "center" }}>
        <div>
          <h1 className="dashboard-card__title">Applicazioni</h1>
          <p className="dashboard-card__subtitle">
            Crea e personalizza i mini gestionali per ogni ente o azienda.
          </p>
        </div>
        <Button as={Link} to="/dashboard/applicazioni/nuova" className="users-edit-btn">
          Nuova applicazione
        </Button>
      </div>

      {error && <FlashAlert variant="danger" onHidden={() => setError("")}>{error}</FlashAlert>}
      {success && <FlashAlert variant="success" onHidden={() => setSuccess("")}>{success}</FlashAlert>}
      {loading && <p>Caricamento...</p>}

      {!loading && applications.length === 0 && (
        <Alert variant="info">Nessuna applicazione creata.</Alert>
      )}

      {!loading && applications.length > 0 && (
        <div className="dashboard-events-list">
          {applications.map((app) => {
            const assigned = gestoriForApp(app.id);
            return (
              <div className="dashboard-event-item" key={app.id}>
                <div>
                  <p className="dashboard-event-item__title">{app.name}</p>
                  <p className="dashboard-event-item__meta">URL: /{app.slug}</p>
                  <p className="dashboard-event-item__meta">{app.site_title}</p>
                  <p className="dashboard-event-item__meta">
                    Gestori: {assigned.length ? assigned.map((g) => g.username).join(", ") : "nessuno"}
                  </p>
                </div>
                <div className="dashboard-event-item__actions">
                  <Link
                    to={`/${app.slug}`}
                    className="dashboard-icon-btn"
                    title="Visita applicazione"
                    aria-label={`Visita ${app.name}`}
                  >
                    <ViewIcon />
                  </Link>
                  <Link
                    to={`/dashboard/applicazioni/${app.id}`}
                    className="dashboard-icon-btn"
                    title="Personalizza applicazione"
                    aria-label={`Personalizza ${app.name}`}
                  >
                    <EditIcon />
                  </Link>
                  <button
                    type="button"
                    className="dashboard-icon-btn"
                    title="Elimina applicazione"
                    aria-label={`Elimina ${app.name}`}
                    onClick={() => {
                      setSuccess("");
                      setDeleteError("");
                      setAppToDelete(app);
                    }}
                  >
                    <DeleteIcon />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Modal show={!!appToDelete} onHide={closeDeleteModal} centered>
        <Modal.Header closeButton={!deleting}>
          <Modal.Title>Elimina applicazione</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {deleteError && <FlashAlert variant="danger" onHidden={() => setDeleteError("")}>{deleteError}</FlashAlert>}
          <p>
            Sei sicuro di voler eliminare <strong>{appToDelete?.name}</strong>?
          </p>
          <p className="dashboard-delete-warning">
            L&apos;operazione è irreversibile: verranno eliminati anche gli eventi
            collegati. Gestori e utenti restano in piattaforma, ma non saranno più
            associati a questa applicazione.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-primary" onClick={closeDeleteModal} disabled={deleting}>
            Annulla
          </Button>
          <Button
            className="users-edit-btn"
            onClick={handleDelete}
            disabled={deleting}
          >
            {deleting ? "Eliminazione..." : "Elimina definitivamente"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default DashboardApplications;
